<script setup>
import { computed, useSlots, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/conversations';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToContactsList', 'toggleBlock']);

const { t } = useI18n();
const slots = useSlots();
const route = useRoute();
const router = useRouter();

const isContactSidebarOpen = ref(false);

const store = useStore();
const contactId = computed(() => route.params.contactId);
const contactConversation = ref(null);
const contactInboxId = ref(null);
const showInboxSelector = ref(false);
const selectedInboxForConversation = ref('');
const isCreatingConversation = ref(false);
const accountId = computed(() => store.getters.getCurrentAccountId);
const inboxes = useMapGetter('inboxes/getInboxes');

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({
    label: inbox.name,
    value: inbox.id,
  }))
);

const fetchContactConversation = async id => {
  if (!id) {
    contactConversation.value = null;
    contactInboxId.value = null;
    return;
  }
  try {
    const { data } = await ContactAPI.getConversations(id);
    const conversations = data?.payload || [];
    contactConversation.value =
      conversations.length > 0 ? conversations[0] : null;
    if (!contactConversation.value) {
      const showResp = await ContactAPI.getContactableInboxes(id);
      const contactInboxes = showResp.data?.payload || [];
      contactInboxId.value =
        contactInboxes.length > 0 ? contactInboxes[0].inbox?.id : null;
    }
  } catch {
    contactConversation.value = null;
    contactInboxId.value = null;
  }
};

const contactConversationUrl = computed(() => {
  if (!contactConversation.value) return null;
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: contactConversation.value.id,
    })
  );
});

const navigateToConversation = conversationId => {
  router.push(
    frontendURL(
      conversationUrl({ accountId: accountId.value, id: conversationId })
    )
  );
};

const createConversationInInbox = async inboxId => {
  isCreatingConversation.value = true;
  try {
    const contact = props.selectedContact;
    const payload = {
      inbox_id: inboxId,
      contact_id: contactId.value,
      assignee_id: store.getters.getCurrentUser?.id,
    };
    if (contact?.phoneNumber) {
      payload.source_id = contact.phoneNumber.replace('+', '');
    }
    const { data } = await ConversationApi.create(payload);
    contactConversation.value = data;
    navigateToConversation(data.id);
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.CONVERSATION_CREATE_ERROR'));
  } finally {
    isCreatingConversation.value = false;
  }
};

const handleGoToConversation = () => {
  if (contactConversationUrl.value) {
    router.push(contactConversationUrl.value);
    return;
  }
  if (contactInboxId.value) {
    createConversationInInbox(contactInboxId.value);
    return;
  }
  showInboxSelector.value = true;
};

const handleInboxSelected = async inboxId => {
  selectedInboxForConversation.value = inboxId;
  showInboxSelector.value = false;
  contactInboxId.value = inboxId;
  await createConversationInInbox(inboxId);
};

watch(contactId, newId => fetchContactConversation(newId), { immediate: true });

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = () => {
  emit('toggleBlock', isContactBlocked.value);
};

const handleConversationSidebarToggle = () => {
  isContactSidebarOpen.value = !isContactSidebarOpen.value;
};

const closeMobileSidebar = () => {
  if (!isContactSidebarOpen.value) return;
  isContactSidebarOpen.value = false;
};
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden justify-evenly bg-n-surface-1"
  >
    <div
      class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
    >
      <header class="sticky top-0 z-10 px-6 3xl:px-0">
        <div class="w-full mx-auto max-w-[40.625rem]">
          <div
            class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
          >
            <Breadcrumb
              :items="breadcrumbItems"
              @click="handleBreadcrumbClick"
            />
            <div class="flex items-center gap-2">
              <Button
                :label="
                  !isContactBlocked
                    ? $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
                    : $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
                "
                size="sm"
                slate
                :is-loading="isUpdating"
                :disabled="isUpdating"
                @click="toggleBlock"
              />
              <VoiceCallButton
                :phone="selectedContact?.phoneNumber"
                :contact-id="contactId"
                :label="$t('CONTACT_PANEL.CALL')"
                size="sm"
              />
              <button
                class="inline-flex items-center gap-2 rounded-lg bg-woot-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-woot-600 transition-colors disabled:opacity-50"
                :disabled="isCreatingConversation"
                @click="handleGoToConversation"
              >
                <span
                  v-if="isCreatingConversation"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                <span v-else class="i-lucide-message-circle size-4" />
                {{ $t('CONTACTS_LAYOUT.DETAILS.GO_TO_CONVERSATION') }}
              </button>
              <ComboBox
                v-if="showInboxSelector"
                v-model="selectedInboxForConversation"
                :options="inboxOptions"
                :placeholder="
                  $t('CONTACTS_LAYOUT.DETAILS.SELECT_INBOX_PLACEHOLDER')
                "
                class="[&>div>button]:h-8"
                @update:model-value="handleInboxSelected"
              />
            </div>
          </div>
        </div>
      </header>
      <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
        <div class="w-full py-4 mx-auto max-w-[40.625rem]">
          <slot name="default" />
        </div>
      </main>
    </div>

    <!-- Desktop sidebar -->
    <div
      v-if="slots.sidebar"
      class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
    >
      <slot name="sidebar" />
    </div>

    <!-- Mobile sidebar container -->
    <div
      v-if="slots.sidebar"
      class="lg:hidden fixed top-0 ltr:right-0 rtl:left-0 h-full z-50 flex justify-end transition-all duration-200 ease-in-out"
      :class="isContactSidebarOpen ? 'w-full' : 'w-16'"
    >
      <!-- Toggle button -->
      <div
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#contact-sidebar-content'] },
        ]"
        class="flex items-start p-1 w-fit h-fit relative order-1 xs:top-24 top-28 transition-all bg-n-solid-2 border border-n-weak duration-500 ease-in-out"
        :class="[
          isContactSidebarOpen
            ? 'justify-end ltr:rounded-l-full rtl:rounded-r-full ltr:rounded-r-none rtl:rounded-l-none'
            : 'justify-center rounded-full ltr:mr-6 rtl:ml-6',
        ]"
      >
        <Button
          ghost
          slate
          sm
          class="!rounded-full rtl:rotate-180"
          :class="{ 'bg-n-alpha-2': isContactSidebarOpen }"
          :icon="
            isContactSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          data-contact-sidebar-toggle
          @click="handleConversationSidebarToggle"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
        enter-to-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-from-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
      >
        <div
          v-if="isContactSidebarOpen"
          id="contact-sidebar-content"
          class="order-2 w-[85%] sm:w-[50%] bg-n-solid-2 ltr:border-l rtl:border-r border-n-weak overflow-y-auto py-6 shadow-lg"
        >
          <slot name="sidebar" />
        </div>
      </Transition>
    </div>
  </section>
</template>
